import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, inject, input, viewChild } from "@angular/core";
import { outputToObservable, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IEndpoint, StreamTypes } from "@auvious/rtc";
import { firstValueFrom, merge } from "rxjs";
import { filter } from "rxjs/operators";

import {
  COLOR,
  ColorEnum,
  SketchToolEnum,
  VideoFacingModeEnum,
} from "../../../core-ui.enums";
import {
  ConversationNotification,
  IArea,
  INotificationEvent,
  IStreamMetadata,
} from "../../../models";
import { SketchMetadata } from "../../../models/Metadata";
import {
  ArPointerActivatedEvent,
  ArPointerDeactivatedEvent,
  AuviousRtcService,
  ConferenceService,
  debug,
  debugError,
  NotificationService,
  PointerService,
  SketchAvailabilityChangedEvent,
  SketchService,
  StreamState,
} from "../../../services";
import { CobrowseService } from "../../../services/cobrowse.service";
import { CobrowseFrameComponent } from "../../cobrowse-frame/cobrowse-frame.component";
import { TileComponent } from "../../tile/tile.component";

/**
 * The default marker is a light, see-through highlighter that hardly shows on the white screens
 * of mobile apps. On a shared screen the marker keeps its width and takes the solid blue of the arrow.
 */
const DISPLAY_CAPTURE_MARKER = { color: "#3366FF", width: 10 };

@Component({
  selector: "app-sketch-area",
  templateUrl: "./sketch-area.component.html",
  styleUrls: ["./sketch-area.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SketchAreaComponent implements OnInit, OnDestroy {
  private sketchService = inject(SketchService);
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private cd = inject(ChangeDetectorRef);
  private rtcService = inject(AuviousRtcService);
  private pointerService = inject(PointerService);
  private conferenceService = inject(ConferenceService);
  private cobrowseService = inject(CobrowseService);
  private renderer = inject(Renderer2);
  private notification = inject(NotificationService);
  private tile = inject(TileComponent, { optional: true });
  private cobrowseFrame = inject(CobrowseFrameComponent, { optional: true });
  private destroyRef = inject(DestroyRef);

  @Input()
  set viewSize(value: IArea) {
    this._viewSize = value;

    if (this._id) {
      this.sketchService.resize(value, this._id);
    } else if (value.width > 0 && value.height > 0) {
      this.connectOnViewSizeAvailable();
    }
  }

  get viewSize() {
    return this._viewSize;
  }

  @Input()
  set scaleRatio(value: number) {
    this._scaleRatio = value;

    if (this._id) {
      this.sketchService.scale(value, this._id);
    }
  }

  get scaleRatio() {
    return this._scaleRatio;
  }

  readonly endpoint = input<IEndpoint>(undefined);
  /** the annotated stream. A screen shared in co-browse only has a type and a correlation id */
  readonly stream = input<Partial<StreamState>>(undefined);

  /**
   * Annotates the screen a co-browse customer shares. A new sketch starts once the view
   * has a size and ends with the view. The customer keeps their own drawings.
   */
  readonly displayCapture = input(false);

  /**
   * syns view size & coordinates to a base view size that is common for both local and remotes,
   * in case local and remotes have different window sizes.
   * In co-browse it is not needed. scale and actual width/height does the trick.
   * In tiles, we do not have the same area dimensions, so we need to sync.
   */
  readonly sync = input(false);

  @Output() connected: EventEmitter<string> = new EventEmitter();
  @Output() ready: EventEmitter<HTMLCanvasElement> = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() ended: EventEmitter<string> = new EventEmitter();

  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("canvas");

  activeTool: SketchToolEnum;

  private _id: string;
  private _viewSize: IArea;
  private _scaleRatio = 1;
  private _cobrowseOn = false;
  private _pointColors: { [endpoint: string]: string } = {};
  private _isSettingUpExistingSketch = false;
  private _isStartingDisplayCaptureSketch = false;
  private _pendingConnectionId: string;
  private _notification: INotificationEvent;

  @HostBinding("class") get class() {
    return {
      "sketch-tool-marker": this.activeTool === SketchToolEnum.marker,
      "sketch-tool-arrow": this.activeTool === SketchToolEnum.arrow,
      "sketch-tool-eraser": this.activeTool === SketchToolEnum.eraser,
      // 'sketch-mirror': !this._isRequester
    };
  }

  @HostListener("mousewheel", ["$event"])
  private onScroll(event: Event): void {
    if (!this.cobrowseFrame) {
      return;
    }
    if (!this._notification) {
      this._notification = this.notification.info("Scrolling is disabled", {
        body: "You cannot scroll while you use one of the annotation controls. Stop annotating to resume scrolling.",
        ttl: 3000,
      });
      setTimeout(() => {
        this._notification = undefined;
      }, 3000);
      // this._notification = new ConversationNotification(
      //   "Scrolling is disabled",
      //   "You cannot scroll while you use one of the annotation controls. Stop annotating to resume scrolling.",
      //   Util.uuidgen()
      // );
      // (this._notification as ConversationNotification).onClicked(() => {
      //   this.notification.dismiss(this._notification);
      //   this._notification = null;
      // });
      // (this._notification as ConversationNotification).onDismissed(() => {
      //   this._notification = null;
      // });
      // this.notification.notify(this._notification);
    }
  }

  ngOnInit(): void {
    if (this.cobrowseFrame) {
      outputToObservable(this.cobrowseFrame.viewSizeChanged).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.renderer.setStyle(
          this.host.nativeElement,
          "width",
          value.sketch.width
        );
        this.renderer.setStyle(
          this.host.nativeElement,
          "height",
          value.sketch.height
        );
      });
    }
    this.tile?.viewSizeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      // the host box is owned by the tile stylesheet, we only follow the canvas size
      // debug('tile viewSize changed: ', value);
      this.viewSize = value;
    });
    this.sketchService.toolSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tool) => {
      this.activeTool = tool;
      this.cd.detectChanges();
    });
    this.cobrowseService.started$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (data) => {
      this._cobrowseOn = true;
    });
    this.cobrowseService.endRequest$
      .pipe(
        filter((_) => !this.tile && !!this._id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        debug("sketch-area:cobrowseService:endRequest$");
        if (this.displayCapture()) {
          this.endDisplayCaptureSketch();
          return;
        }
        this.updateCobrowseConferenceMetadata(false, null);
        this.notifyCobrowseParticipant(false);
        this._cobrowseOn = false;
      });

    merge(
      this.conferenceService.conferenceMetadataSet$,
      this.cobrowseService.conferenceMetadataSet$
    )
      .pipe(
        filter((meta) => meta instanceof SketchMetadata),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((meta: SketchMetadata) => {
        debug(
          "sketch-area:conferenceMetadataSet$",
          meta,
          this.cobrowseService.target
        );
        // try to find active sketch
        const connectionId = Object.keys(meta.sketchMap).find((key) =>
          this.isOnCobrowse
            ? true // on a co-browse session the endpoint is the remote sender
            : // todo: be aware of race condition: endpoint joined may come after metadataSet
              // meta.sketchMap[key].target?.endpoint ===
              // this.cobrowseService.target.endpoint

              // on a ar session the endpoint is the stream originator
              meta.sketchMap[key].target?.endpoint ===
                this.endpoint()?.endpoint &&
              meta.sketchMap[key].correlationId === this.stream()?.correlationId
        );

        if (!connectionId) {
          return;
        }

        // a shared screen starts its own sketch instead of joining one
        if (
          meta.sketchMap[connectionId].enabled &&
          !this._id &&
          !this.displayCapture()
        ) {
          this.setupExistingSketch(connectionId);
        } else if (
          connectionId === this._id &&
          !meta.sketchMap[connectionId].enabled
        ) {
          this.sketchService.terminate(connectionId);
          this.cleanUp();
        }
      });

    this.pointerService.pointAreaChange$
      .pipe(
        filter(
          (event) =>
            // I am the requester and this is the stream requested
            event.senderUserEndpointId === this.rtcService.endpoint() &&
            event.targetUserEndpointId === this.endpoint()?.endpoint &&
            this.tile?.stream.correlationId ===
              event.targetStreamCorrelationId
          // this.tile?.stream.type === event.targetStreamType
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (event) => {
        let target;
        switch (event.type) {
          case ArPointerActivatedEvent.type:
            try {
              // if we started an AR Session, start a sketch session as well
              await this.connectToNewSketch();
              this.mirrorCanvas(this._id);
              target = {
                endpoint: event.targetUserEndpointId,
                username: event.targetUserId,
              };
              this.updateConferenceMetadata(true, target);
              // no need to notify participants. rely on conference metadata set
              // this.notifyConferenceParticipants(true, target);
            } catch (ex) {
              debugError(ex);
            }
            break;
          case ArPointerDeactivatedEvent.type:
            target = {
              endpoint: event.targetUserEndpointId,
              username: event.targetUserId,
            };
            this.updateConferenceMetadata(false, target);
            // this.notifyConferenceParticipants(false, target);
            this.sketchService.selectTool(null);
            this.sketchService.terminate(this._id);
            // deactivate sketch tools
            this.cleanUp();
            break;
        }
        this.cd.detectChanges();
      });

    this.pointerService.pointColorsChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((colors) => {
      this._pointColors = colors;
      this.updateToolsColor(this._id);
    });

    // listen to events for remote sketch initiation
    firstValueFrom(this.rtcService.getEventObservableAvailable()).then(
      (eventObservable) => {
        eventObservable
          .pipe(
            filter(
              (data) =>
                SketchAvailabilityChangedEvent.type === data?.payload?.type &&
                (data.payload as SketchAvailabilityChangedEvent)
                  .senderUserId !== this.rtcService.identity() &&
                (data.payload as SketchAvailabilityChangedEvent)
                  .targetEndpointId === this.endpoint()?.endpoint &&
                (data.payload as SketchAvailabilityChangedEvent)
                  .targetMediaType === this.stream()?.type
            ),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(async (data) => {
            debug(
              "sketch-area:SketchAvailabilityChangedEvent",
              data.payload,
              this.endpoint()
            );
            const payload = data.payload as SketchAvailabilityChangedEvent;
            if (payload.available) {
              this.setupExistingSketch(payload.connectionId);
            } else {
              this.sketchService.terminate(payload.connectionId);
              this.cleanUp();
            }
          });
      }
    );
  }

  ngOnDestroy() {
    this._pendingConnectionId = null;
    // the shared screen is gone
    if (this.displayCapture() && this._id) {
      this.endDisplayCaptureSketch();
    }
  }

  private async setupExistingSketch(connectionId: string) {
    try {
      if (this._isSettingUpExistingSketch) {
        return;
      }
      // wait for a viewSize to be set to connect, otherwise the canvas is of zero size
      if (
        !this._viewSize ||
        this._viewSize.height === 0 ||
        this._viewSize.width === 0
      ) {
        this._pendingConnectionId = connectionId;
        return;
      }

      this._isSettingUpExistingSketch = true;
      await this.connectToExistingSketch(connectionId);

      // only mirror for tile parent
      if (!this.isOnCobrowse) {
        this.mirrorCanvas(connectionId);
      } else {
        this.sketchService.clear(connectionId);
      }
    } catch (ex) {
      debugError(ex);
      this.sketchService.terminate(connectionId);
      this.cleanUp();
    } finally {
      this._isSettingUpExistingSketch = false;
    }
  }

  private async connectOnViewSizeAvailable() {
    if (this._pendingConnectionId) {
      debug("sketch-area:connect existing on resize", this._viewSize);
      this.setupExistingSketch(this._pendingConnectionId);
      this._pendingConnectionId = null;
    } else if (this.displayCapture()) {
      await this.startDisplayCaptureSketch();
    }
    // used to be on cobrowse.resized$ observable
    else if (
      this._cobrowseOn &&
      !this._id &&
      !this._isSettingUpExistingSketch &&
      !this.tile
    ) {
      debug("sketch-area:connect new on resize", this._viewSize);
      await this.connectToNewSketch();
      this.updateCobrowseConferenceMetadata(true, this.cobrowseService.target);
      this.notifyCobrowseParticipant(true);
    }
    // when coming from a refresh and we find a existing AR-pointer session
    else if (
      this.pointerService.isTarget(
        this.endpoint().endpoint,
        this.stream().type,
        this.stream().correlationId
      ) &&
      !this._id &&
      !this._isSettingUpExistingSketch
    ) {
      debug("sketch-area:connect existing on ar-pointer refresh");

      const meta = this.sketchService.getConferenceMetadata();
      let activeSketchId;
      Object.keys(meta.sketchMap).forEach((sketchId) => {
        const sketch = meta.sketchMap[sketchId];
        if (
          sketch.enabled &&
          sketch.target.endpoint === this.endpoint().endpoint &&
          sketch.correlationId === this.stream().correlationId
        ) {
          activeSketchId = sketchId;
        }
      });
      if (activeSketchId) {
        this.setupExistingSketch(activeSketchId);
      }
    }
  }

  private async connectToExistingSketch(id: string) {
    this._id = await this.sketchService.connect(
      this.canvasRef().nativeElement,
      this.sync(),
      this.endpoint()?.endpoint,
      id
    );
    debug("sketch-area: connected to existing sketch ", id);
    this.prepareCanvas(id);
    this.connected.emit(this._id);
    this.cd.markForCheck();
  }

  private async connectToNewSketch() {
    this._id = await this.sketchService.connect(
      this.canvasRef().nativeElement,
      this.sync(),
      this.endpoint()?.endpoint
    );
    debug("sketch-area: connected to new sketch ", this._id);
    this.prepareCanvas(this._id);
    this.connected.emit(this._id);
    this.cd.markForCheck();
  }

  private async startDisplayCaptureSketch() {
    // the view resizes a few times while the sketch connects
    if (this._isStartingDisplayCaptureSketch) {
      return;
    }
    this._isStartingDisplayCaptureSketch = true;
    try {
      debug("sketch-area:connect new on display capture", this._viewSize);
      await this.connectToNewSketch();

      // the shared screen went away while connecting
      if (this.destroyRef.destroyed) {
        this.sketchService.terminate(this._id, false);
        return;
      }

      const { username, endpoint } = this.endpoint();
      this.updateCobrowseConferenceMetadata(true, { username, endpoint });
      this.notifyCobrowseParticipant(true);
    } catch (ex) {
      debugError(ex);
    } finally {
      this._isStartingDisplayCaptureSketch = false;
    }
  }

  private endDisplayCaptureSketch() {
    this.updateCobrowseConferenceMetadata(false, null);
    this.notifyCobrowseParticipant(false);
    // clearing would also erase the drawings of the customer
    this.sketchService.terminate(this._id, false);
    this._id = null;
  }

  private prepareCanvas(id) {
    if (this.viewSize) {
      this.sketchService.resize(this.viewSize, id);
    }
    if (!!this.scaleRatio && !this.sync()) {
      this.sketchService.scale(this.scaleRatio, id);
    }
    if (this.displayCapture()) {
      this.sketchService.setMarkerColor(
        DISPLAY_CAPTURE_MARKER.color,
        id,
        DISPLAY_CAPTURE_MARKER.width
      );
    }
    debug("prepare canvas point color change");
    // change colors to sketch
    this.updateToolsColor(id);
  }

  private updateToolsColor(id: string) {
    if (!id) {
      return;
    }
    const myColor = this._pointColors[this.rtcService.endpoint()];
    if (!myColor) {
      return;
    }
    this.sketchService.setMarkerColor(myColor, id);
    this.sketchService.setArrowColor(myColor, id);
  }

  private mirrorCanvas(id) {
    // if the target is not me (my stream is mirrored) or the target is me and my stream is environment
    // mirror the canvas
    const endpoint = this.endpoint();
    const isMe = endpoint?.endpoint === this.rtcService.endpoint();
    const streamValue = this.stream();
    const isScreen = streamValue?.type === StreamTypes.SCREEN;
    const myStreams = this.conferenceService.getStreamsForEndpoint(
      endpoint?.endpoint
    );
    const myStream = Object.values(myStreams).find(
      (stream) =>
        [StreamTypes.CAM, StreamTypes.VIDEO].includes(stream.type) &&
        (stream.getMetadata() as IStreamMetadata).primary
    );
    if (!myStream) {
      return;
    }
    const facingMode = streamValue?.video.facingMode;

    if (
      !isScreen &&
      (!isMe || (isMe && facingMode === VideoFacingModeEnum.Environment))
    ) {
      // if (
      //   (!isMe && !isScreen) ||
      //   (isMe && !isScreen && facingMode === VideoFacingModeEnum.Environment)
      // )
      this.sketchService.mirror(id);
    }
  }

  private updateConferenceMetadata(enabled: boolean, target: IEndpoint) {
    const meta = this.sketchService.getConferenceMetadata();
    const stream = this.stream();
    meta.setEnabled(
      this._id,
      enabled,
      target,
      stream?.type,
      stream?.correlationId
    );
    this.sketchService.setConferenceMetadata(meta);

    // update session state
    this.conferenceService.setConferenceMetadata(meta);
  }

  private updateCobrowseConferenceMetadata(
    enabled: boolean,
    target: IEndpoint
  ) {
    const metadata = this.sketchService.getConferenceMetadata();
    // a co-browsed page has no stream, a shared screen does
    const stream = this.stream();
    metadata.setEnabled(
      this._id,
      enabled,
      target,
      stream?.type ?? "unknown",
      stream?.correlationId
    );
    this.sketchService.setConferenceMetadata(metadata);

    // update session state
    this.cobrowseService.context === "conference"
      ? this.conferenceService.setConferenceMetadata(metadata)
      : this.cobrowseService.setConferenceMetadata(metadata);
  }

  private notifyCobrowseParticipant(enabled: boolean) {
    if (this.cobrowseService.target) {
      this.sketchService.notifyAvailabilityChanged(
        this.cobrowseService.target,
        this.cobrowseService.target,
        this._id,
        enabled,
        this.stream()?.type
      );
    }
  }

  private notifyConferenceParticipants(
    enabled: boolean,
    sketchTarget: IEndpoint
  ) {
    // notify remote participants
    const participants = this.conferenceService.getParticipants();
    Object.keys(participants).forEach((key) => {
      this.sketchService.notifyAvailabilityChanged(
        participants[key],
        sketchTarget,
        this._id,
        enabled,
        this.stream()?.type
      );
    });
  }

  private cleanUp() {
    this._id = null;
    this.ended.emit();
  }

  get container(): HTMLDivElement {
    return this.host.nativeElement;
  }

  private get isOnCobrowse() {
    return !!this.cobrowseFrame;
  }
}
